import { useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { dateIt, num } from "@/lib/format";

const pct = (v) => `${v.toLocaleString("it-IT", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}%`;

const ORIZZONTI = [["giorno", "vs giorno prec."], ["mese_1", "vs 1 mese"],
  ["mesi_3", "vs 3 mesi"], ["mesi_6", "vs 6 mesi"], ["anno_1", "vs 1 anno"]];

function Trend({ t, label, testId }) {
  if (!t) return null;
  const su = t.delta_bp > 0;
  const zero = t.delta_bp === 0;
  const Icona = zero ? Minus : su ? ArrowUpRight : ArrowDownRight;
  const colore = zero ? "text-slate-500" : su ? "text-red-600" : "text-emerald-600";
  return (
    <div className="flex items-center justify-between gap-2 text-xs" data-testid={testId}>
      <span className="text-slate-500">{label} <span className="text-slate-400">({dateIt(t.data)})</span></span>
      <span className="inline-flex items-center gap-2">
        <span className="font-mono text-slate-700">{pct(t.valore)}</span>
        <span className={`inline-flex items-center gap-0.5 font-mono font-semibold ${colore}`}
          title="scostamento in punti base (1 bp = 0,01%)">
          <Icona size={12} />{su ? "+" : ""}{num(t.delta_bp, 1)} bp
        </span>
      </span>
    </div>
  );
}

/** Box EUR IRS 3/5/10 anni con trend: fonte Teleborsa, storico accumulato giorno per giorno. */
export function BoxIrs({ admin }) {
  const [d, setD] = useState(null);
  const [agg, setAgg] = useState(false);
  const carica = () => api.get("/news/irs").then((r) => setD(r.data)).catch(() => setD({ disponibile: false }));
  useEffect(() => { carica(); }, []);

  const aggiorna = async () => {
    setAgg(true);
    try {
      const r = await api.post("/news/irs/aggiorna");
      setD(r.data);
      toast.success(`IRS aggiornato · ${r.data.date} chiusure lette`);
    } catch (e) { toast.error(apiError(e)); } finally { setAgg(false); }
  };

  if (!d) return null;
  return (
    <Card className="p-4 mb-6" data-testid="box-irs">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
          Tassi EUR IRS{d.disponibile ? ` · fixing del ${dateIt(d.data)}` : ""}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-400">
          {d.storico_dal && <span>storico dal {dateIt(d.storico_dal)} · fonte {d.fonte}</span>}
          {admin && (
            <button onClick={aggiorna} disabled={agg} data-testid="irs-aggiorna"
              className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 disabled:opacity-50
                transition-colors duration-200">
              <RefreshCw size={12} className={agg ? "animate-spin" : ""} /> aggiorna
            </button>
          )}
        </div>
      </div>
      {!d.disponibile ? (
        <div className="text-sm text-slate-500" data-testid="irs-vuoto">
          Nessuna rilevazione ancora disponibile: il primo aggiornamento automatico avviene pochi minuti dopo l'avvio.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {d.tassi.map((t) => (
            <div key={t.chiave} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4"
              data-testid={`irs-${t.chiave}`}>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">IRS {t.label}</span>
                <span className="font-mono text-2xl font-semibold text-slate-900" data-testid={`irs-${t.chiave}-valore`}>
                  {t.valore === null ? "n/d" : pct(t.valore)}
                </span>
              </div>
              <div className="space-y-1">
                {ORIZZONTI.map(([k, label]) => (
                  <Trend key={k} t={t.trend[k]} label={label} testId={`irs-${t.chiave}-${k}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
